// Types owned by @kinnet-studio/pattern — re-exported verbatim so downstream
// code that imports from this module continues to work unchanged.
export type {
    TbcId,
    BaseId,
    TopId,
    TbcType,
    TbcPoint,
    GuideLineSnap,
    GuideCircleSnap,
    GuideBezierSnap,
    GuideSnap,
    TopAttachSnap,
    TopSnap,
    SnapBinding,
    Base,
    Top,
    Tbc,
} from '@kinnet-studio/pattern';
export { TBC_TYPES } from '@kinnet-studio/pattern';

/** Base stitch types that may be used as an increase/decrease. Lives here (the
 *  pure types module) so non-UI modules — the guide combination parser — can
 *  import it without pulling in icon/React deps. The rail re-exports it.
 *  Base types only: asymmetric legs always render with `-full` geometry. */
export const ASYM_ELIGIBLE_TYPES: TbcType[] = ['single-crochet', 'double-crochet', 'half-double-crochet', 'treble-crochet', 'double-treble-crochet', 'front-post-single-crochet', 'front-post-half-double-crochet', 'front-post-double-crochet', 'front-post-treble-crochet', 'front-post-double-treble-crochet', 'back-post-single-crochet', 'back-post-half-double-crochet', 'back-post-double-crochet', 'back-post-treble-crochet', 'back-post-double-treble-crochet'];

/** Stitch types offered as the member of a gather stitch (cluster/popcorn),
 *  ordered by height. Deliberately narrower than ASYM_ELIGIBLE_TYPES: gathers
 *  need a post tall enough to bow, so sc and the post-stitch variants are out. */
export const GATHER_MEMBER_TYPES: TbcType[] = ['half-double-crochet', 'double-crochet', 'treble-crochet', 'double-treble-crochet'];

import type {
    TbcId,
    TbcType,
    TbcPoint,
    TopAttachSnap,
    GuideSnap,
    GuideLineSnap,
    GuideCircleSnap,
    BaseId,
    TopId,
    Base,
    SnapBinding,
    Tbc,
} from '@kinnet-studio/pattern';

/** Shaped TBCs have a base and a top handle that define the stitch's
 *  position, size, and orientation; rect-kind instances are gizmo-edited
 *  (symmetric instances opt in via isRectTbc).
 *
 *  Variants — differ only in the symbol drawn and where the bounding box sits
 *  relative to the two handles; the sized interaction rules (sizing-handle
 *  perpendicular widening) apply to all:
 *  - 'chain' / 'single-crochet' / 'single-crochet-x':
 *        midpoint base — bounding box centered at base; top at one end of
 *        the minor axis. |base→top| = b (semi-minor).
 *  - '<name>-full':
 *        full base — base and top at OPPOSITE ends of the minor axis;
 *        bounding box centered at their midpoint. |base→top| = 2b.
 *  Members migrated to the rect kind (see RECT_TBC_TYPES in rect-tbc.ts) opt
 *  out per-instance via isRectTbc — symmetric instances are gizmo-edited; only
 *  their asymmetric instances use these shaped rules. */
export function isShapedType(type: TbcType): boolean {
    // Clusters render via their own petal path and (for now) have no sizing
    // knobs, so they must NOT be treated as "shaped" — otherwise the renderer
    // hides their base/top handles in sizing mode, leaving no interactive
    // handles at all. Treat them like the leg-cap family here.
    if (type === 'cluster' || type === 'popcorn') return false;
    return !isLegCapType(type) && type !== 'slip-stitch';
}

/** The "leg + cap" standing-stitch family: a vertical leg with a top-cap bar
 *  (double crochet, half double crochet, treble crochet, double treble crochet,
 *  and the front-post & back-post series which share the same rendering path).
 *  These are non-shaped (pad both ends, anchor the symbol identically) and render
 *  via the shared leg-cap path. */
export function isLegCapType(type: TbcType): type is 'double-crochet' | 'half-double-crochet' | 'treble-crochet' | 'double-treble-crochet' | 'front-post-single-crochet' | 'front-post-half-double-crochet' | 'front-post-double-crochet' | 'front-post-treble-crochet' | 'front-post-double-treble-crochet' | 'back-post-single-crochet' | 'back-post-half-double-crochet' | 'back-post-double-crochet' | 'back-post-treble-crochet' | 'back-post-double-treble-crochet' {
    return type === 'double-crochet' || type === 'half-double-crochet' || type === 'treble-crochet' || type === 'double-treble-crochet'
        || type === 'front-post-single-crochet' || type === 'front-post-half-double-crochet' || type === 'front-post-double-crochet' || type === 'front-post-treble-crochet' || type === 'front-post-double-treble-crochet'
        || type === 'back-post-single-crochet' || type === 'back-post-half-double-crochet' || type === 'back-post-double-crochet' || type === 'back-post-treble-crochet' || type === 'back-post-double-treble-crochet';
}

/** The post family (front-post and back-post) — the leg-cap stitches worked around
 *  the post, drawn with the "around-the-post" hook centered on the base handle
 *  (front opens upper-left, back is the mirror). A subset of the leg-cap family; used
 *  to anchor the hook to the true base handle (the base end is NOT padded, so the
 *  handle sits at the hook's center). */
export function isPostType(type: TbcType): boolean {
    return type.startsWith('front-post-') || type.startsWith('back-post-');
}

/** True for the "-full" variants whose base sits at one corner of the
 *  bounding box rather than at its center. Used by the engine and the
 *  pixi layer to pick b = |base→top|/2 instead of b = |base→top|. */
export function isFullVariant(type: TbcType): boolean {
    return type.endsWith('-full');
}

/** The normal/base type of a shape family: strips the `-full` suffix so a
 *  full variant maps to its base sibling (e.g. 'single-crochet-full' →
 *  'single-crochet'). Non-full types are returned unchanged. Every `-full`
 *  variant's base is itself a valid `TbcType`. */
export function baseShapeType(type: TbcType): TbcType {
    return (type.endsWith('-full') ? type.slice(0, -'-full'.length) : type) as TbcType;
}

/** True when a stitch of this type can be a chain of a SPACE — the chain
 *  family (`chain` and `chain-full`). Single source of truth for the
 *  chain-space eligibility rule; every space gate must use this so they
 *  cannot drift. */
export function isChainSpaceType(type: TbcType): boolean {
    return baseShapeType(type) === 'chain';
}

/** Two TBC types belong to the same family if they are the same type or
 *  are the normal/`-full` siblings of the same kind (e.g. 'single-crochet'
 *  and 'single-crochet-full'). Used to decide when per-instance properties
 *  like widthScale should be carried across — `defaultWidthScale` is
 *  family-keyed, so the same numeric widthScale on a normal and a full
 *  variant means "the same proportional deviation from each variant's
 *  default", which is the right thing to preserve. */
export function sameShapeFamily(a: TbcType, b: TbcType): boolean {
    return baseShapeType(a) === baseShapeType(b);
}

export type TbcHandle = 'base' | 'top';

/** A faint snap-target affordance shown while dragging a handle in edit mode.
 *  `shape` mirrors the anchor visual language: 'dot' = filled (a base target),
 *  'ring' = hollow (a top target). */
export type GhostTarget = { point: TbcPoint; shape: 'dot' | 'ring' };

/** The specific base/top a handle drag grabbed. */
export type GrabbedHandle =
    | { id: TbcId; handle: 'base'; baseId: BaseId }
    | { id: TbcId; handle: 'top'; topId: TopId };

/** Compile-time-exhaustive registry of the guide snap kinds. Keyed by
 *  `GuideSnap['kind']`, so when @kinnet-studio/pattern grows the union this
 *  Record fails to typecheck until the new kind is added here — the predicate
 *  below (and everything derived from GUIDE_SNAP_KINDS) then can't go stale
 *  the way the inline kind lists behind SP-31 did. */
const GUIDE_SNAP_KIND: Record<GuideSnap['kind'], true> = {
    'guide-line': true,
    'guide-circle': true,
    'guide-bezier': true,
};

/** Every guide snap kind, derived from the exhaustive registry above. Iterate
 *  this in tests/predicates instead of hand-listing kinds. */
export const GUIDE_SNAP_KINDS = Object.keys(GUIDE_SNAP_KIND) as ReadonlyArray<GuideSnap['kind']>;

/** Which guide snap kinds every replica resolves to the same point, to the
 *  last bit (see {@link isExactlyResolvedGuideSnap}). Exhaustive, as above. */
const EXACTLY_RESOLVED_GUIDE_SNAP_KIND: Record<GuideSnap['kind'], boolean> = {
    'guide-line': true, // linear interpolation: IEEE arithmetic only
    'guide-circle': false, // cos / sin, which engines may round differently
    'guide-bezier': false, // an arc-length lookup built from sampled lengths
};

/** Same exhaustive-registry device for the parent-attach kinds. */
const PARENT_ATTACH_KIND: Record<TopAttachSnap['kind'], true> = {
    'tbc-top': true,
};

/** True when a base's snap rides another stitch's top. Use this instead of
 *  comparing `kind` directly: any site asking "is this handle attached to a
 *  parent?" must treat all parent-attach kinds the same. Compare `kind`
 *  directly only when consuming kind-specific payload (`parentId`/`topId`)
 *  or constructing a binding. When adding a new binding kind, decide
 *  whether it is a parent attach — the PARENT_ATTACH_KIND registry above
 *  won't compile until you do. */
export function isParentAttach(snap: SnapBinding | null | undefined): snap is TopAttachSnap {
    return snap != null && snap.kind in PARENT_ATTACH_KIND;
}

/** True when a handle is snapped to a guide's geometry (line / arc / bezier) —
 *  as opposed to a parent attachment ({@link isParentAttach}) or free. Reads
 *  the exhaustive GUIDE_SNAP_KIND registry, so it covers every guide kind by
 *  construction. */
export function isGuideSnap(snap: SnapBinding | null | undefined): snap is GuideSnap {
    return snap != null && snap.kind in GUIDE_SNAP_KIND;
}

/** True for a guide snap whose point every replica computes from the guide
 *  and the snap to the same last bit (a line's), so the point stored for it
 *  can be required to equal that computation exactly. False for a ring's or
 *  a curve's, which may differ in the last bit between engines. */
export function isExactlyResolvedGuideSnap(snap: SnapBinding | null | undefined): snap is GuideSnap {
    return isGuideSnap(snap) && EXACTLY_RESOLVED_GUIDE_SNAP_KIND[snap.kind];
}

/** True for the guide snap kinds whose along-guide parameter has endpoint
 *  semantics the distribute/redistribute ops can space between: a line's `t`
 *  or a ring's `angle`. Bezier snaps are DELIBERATELY excluded — the
 *  between-endpoints ops don't implement arc-length spacing on a curve.
 *  This is a named product limitation, not an oversight: if bezier support
 *  lands, delete this predicate's bezier exclusion (and its call sites'
 *  behavior) in one reviewed change. */
export function isLineOrRingSnap(
    snap: SnapBinding | null | undefined,
): snap is GuideLineSnap | GuideCircleSnap {
    return snap != null && (snap.kind === 'guide-line' || snap.kind === 'guide-circle');
}

/** Mint a stable `BaseId`. UUID-based for consistency with other id factories. */
export function newBaseId(): BaseId {
    return `base-${crypto.randomUUID()}`;
}

/** True when a TBC has more than one top (an "increase") or more than one
 *  base (a "decrease"). Use this to gate operations that can't generalize
 *  to asymmetric stitches (chain mode, align ops). Every other operation
 *  iterates `tops`/`bases` naturally. */
export function isAsymmetric(t: Tbc): boolean {
    return t.tops.length > 1 || t.bases.length > 1;
}

/** Mint a stable `TopId`. UUID-based for consistency with other id factories
 *  in the codebase (see `tbc-${crypto.randomUUID()}` / `mac-${crypto.randomUUID()}`). */
export function newTopId(): TopId {
    return `top-${crypto.randomUUID()}`;
}

/** Read the "primary" base of a TBC — its first base. Used by code paths that
 *  pre-date asymmetric stitches and still treat the TBC as having a single
 *  base. */
export function primaryBase(t: Tbc): Base {
    return t.bases[0];
}

/** Locate a base of a (possibly missing) parent by id. Returns null when the
 *  parent doesn't exist or the baseId has been removed. */
export function findBase(parent: Tbc | undefined, baseId: BaseId): Base | null {
    if (parent == null) return null;
    return parent.bases.find(b => b.id === baseId) ?? null;
}

/** Chain ellipse default — a deliberately wide horizontal symbol. */
export const CHAIN_ELLIPSE_RATIO_DEFAULT = 7 / 3;

/** Single-crochet `+`/`×` default — roughly square per standard notation. */
export const SINGLE_CROCHET_RATIO_DEFAULT = 1;

/** Full-variant arm length of the chain symbol. A chain lies flat — its
 *  base→top is the SHORT axis, not a standing-stitch height — so it sits off
 *  the unit grid as a cosmetic ruler. Width derives from this at the fixed 7/3
 *  ratio, preserving the oval shape as it scales. (normal variant = half) */
export const CHAIN_FULL_ARM = 15;

/** World-space height of one stitch-column unit — the "turning-chain unit".
 *  A single crochet is 1 unit tall, a double crochet 3 (≈ 3 turning chains).
 *  DERIVED from the chain's major axis (CHAIN_ELLIPSE_RATIO_DEFAULT · CHAIN_FULL_ARM
 *  = 7/3 · 15 = 35) so resizing the chain moves the whole stitch scale with it —
 *  the unit can never drift out of sync with the chain again. */
export const STITCH_HEIGHT_UNIT = CHAIN_ELLIPSE_RATIO_DEFAULT * CHAIN_FULL_ARM;

/** Slip-stitch's small square-dot leg — its own thing, not on the unit grid. */
export const SLIP_STITCH_ARM = 5;

/** Per-family full-variant height in STITCH_HEIGHT_UNITs, keyed by base family
 *  (see baseShapeType) so normal and -full siblings share one entry. To add a
 *  stitch's real relative height, add one entry here (treble → 4). */
const STITCH_HEIGHT_UNITS: Partial<Record<TbcType, number>> = {
    'single-crochet': 1,
    'single-crochet-x': 1,
    'single-crochet-directional': 1,
    'reverse-single-crochet': 1,
    'reverse-single-crochet-x': 1,
    'twisted-single-crochet': 1,
    'twisted-single-crochet-x': 1,
    'double-crochet': 3,
    'half-double-crochet': 2,
    'treble-crochet': 4,
    'double-treble-crochet': 5,
    'front-post-single-crochet': 1,
    'front-post-half-double-crochet': 2,
    'front-post-double-crochet': 3,
    'front-post-treble-crochet': 4,
    'front-post-double-treble-crochet': 5,
    'back-post-single-crochet': 1,
    'back-post-half-double-crochet': 2,
    'back-post-double-crochet': 3,
    'back-post-treble-crochet': 4,
    'back-post-double-treble-crochet': 5,
};

/** Full-variant arm length (world units) for `type`, before the normal/full
 *  halving. chain & slip-stitch are explicit cosmetic legs; everything else is
 *  units × STITCH_HEIGHT_UNIT. */
function fullVariantArm(type: TbcType): number {
    const base = baseShapeType(type);
    if (base === 'chain') return CHAIN_FULL_ARM;
    if (base === 'slip-stitch') return SLIP_STITCH_ARM;
    return (STITCH_HEIGHT_UNITS[base] ?? 1) * STITCH_HEIGHT_UNIT;
}

/** Default |base→top| for a freshly placed TBC of `type`. Normal
 *  (center-anchored) shaped variants take half the full leg so they render at
 *  the same visual size as their span-anchored -full sibling; full variants and
 *  non-shaped types (double-crochet, slip-stitch) use the full leg. Single
 *  source of truth: everything that places or sizes a stitch delegates here. */
export function defaultLegLength(type: TbcType): number {
    // A cluster's stored leg matches its default member (double-crochet) so a
    // freshly placed cluster has a sensible height regardless of member type.
    if (type === 'cluster' || type === 'popcorn') return fullVariantArm('double-crochet');
    const full = fullVariantArm(type);
    return isShapedType(type) && !isFullVariant(type) ? full / 2 : full;
}

/** Default secondary/primary axis ratio for a shaped TBC when its per-instance
 *  `widthScale` is undefined. Each shaped family ships its own default so the
 *  symbol renders correctly at first placement. */
export function defaultWidthScale(type: TbcType): number {
    switch (type) {
        case 'double-crochet':
        case 'half-double-crochet':
        case 'treble-crochet':
        case 'double-treble-crochet':
        case 'front-post-single-crochet':
        case 'front-post-half-double-crochet':
        case 'front-post-double-crochet':
        case 'front-post-treble-crochet':
        case 'front-post-double-treble-crochet':
        case 'back-post-single-crochet':
        case 'back-post-half-double-crochet':
        case 'back-post-double-crochet':
        case 'back-post-treble-crochet':
        case 'back-post-double-treble-crochet': return 1;
        case 'slip-stitch': return 2; // width = 2·legLength = height ⇒ square (dot)
        case 'chain':
        case 'chain-full':
            return CHAIN_ELLIPSE_RATIO_DEFAULT;
        case 'single-crochet':
        case 'single-crochet-full':
        case 'single-crochet-x':
        case 'single-crochet-x-full':
        case 'single-crochet-directional':
        case 'single-crochet-directional-full':
        case 'reverse-single-crochet':
        case 'reverse-single-crochet-full':
        case 'reverse-single-crochet-x':
        case 'reverse-single-crochet-x-full':
        case 'twisted-single-crochet':
        case 'twisted-single-crochet-full':
        case 'twisted-single-crochet-x':
        case 'twisted-single-crochet-x-full':
            return SINGLE_CROCHET_RATIO_DEFAULT;
        case 'popcorn':
        case 'cluster': return 1;
    }
}
